import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { useApi } from '../hooks/useApi';
import { Calendar, User, Stethoscope, ClipboardList, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

export default function FurtherCheckupsPage() {
  const api = useApi();
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);

  useEffect(() => {
    loadFurtherCheckups();
  }, []);

  const loadFurtherCheckups = async () => {
    setLoading(true);
    try {
      const data = await api.get('/prescriptions/further-checkups');
      setPatients(data);
    } catch (error) {
      console.error('Failed to load further checkups:', error);
      toast.error('Failed to load patients');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (prescriptionId, patientId, newStatus) => {
    setUpdatingId(prescriptionId);
    try {
      await api.put(`/prescriptions/further-checkups/${prescriptionId}/status`, { status: newStatus });
      
      // Update local state
      setPatients(prev => prev.map(p => 
        p.prescription_id === prescriptionId 
          ? { ...p, further_checkup_status: newStatus }
          : p
      ));

      toast.success(`Status updated to ${newStatus}`);

      // If status is "yes", navigate to prescription page for that patient
      if (newStatus === 'yes') {
        navigate(`/patients?patient_id=${patientId}&tab=prescriptions`);
      }
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update status');
    } finally {
      setUpdatingId(null);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      undecided: 'bg-gray-100 text-gray-700',
      no: 'bg-red-100 text-red-700',
      yes: 'bg-green-100 text-green-700'
    };
    return styles[status] || styles.undecided;
  };

  const getStatusLabel = (status) => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-purple-900 mb-2">Further Checkups</h1>
            <p className="text-gray-600">Patients whose appointment dates have passed</p>
          </div>
          <Button variant="outline" onClick={loadFurtherCheckups}>
            <ClipboardList className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
          </div>
        ) : patients.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <ClipboardList className="h-16 w-16 mx-auto mb-4 text-gray-300" />
              <h3 className="text-xl font-semibold mb-2">No Patients Found</h3>
              <p className="text-gray-600">
                No patients with past appointment dates require further checkup decisions
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>Patients Requiring Further Checkup Decision ({patients.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Patient Name</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Doctor Name</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Last Appointment Date</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Further Follow Up</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {patients.map((patient) => (
                      <tr key={patient.prescription_id} className="border-b hover:bg-gray-50">
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-gray-400" />
                            <div>
                              <div className="font-medium">{patient.patient_name}</div>
                              {patient.patient_phone && (
                                <div className="text-sm text-gray-500">{patient.patient_phone}</div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-2">
                            <Stethoscope className="h-4 w-4 text-gray-400" />
                            <div>
                              <div className="font-medium">{patient.doctor_name}</div>
                              {patient.doctor_nmc_number && (
                                <div className="text-sm text-gray-500">NMC: {patient.doctor_nmc_number}</div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-gray-400" />
                            <span>
                              {patient.last_appointment_date 
                                ? format(new Date(patient.last_appointment_date), 'MMM dd, yyyy')
                                : 'N/A'}
                            </span>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <Badge className={getStatusBadge(patient.further_checkup_status)}>
                            {getStatusLabel(patient.further_checkup_status)}
                          </Badge>
                        </td>
                        <td className="py-4 px-4">
                          <Select
                            value={patient.further_checkup_status}
                            onValueChange={(value) => handleStatusChange(
                              patient.prescription_id,
                              patient.patient_id,
                              value
                            )}
                            disabled={updatingId === patient.prescription_id}
                          >
                            <SelectTrigger className="w-36">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="undecided">Undecided</SelectItem>
                              <SelectItem value="no">No</SelectItem>
                              <SelectItem value="yes">Yes</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
